import { withRouter } from 'react-router-dom';
import React from 'react';
import { candidatesStore } from '../CandidatesStore';
import { CandidateProfileBio } from './CandidateProfileBio';
import { Loader } from '../../layout/misc/Loader';
import { CandidateProfileSections } from './CandidateProfileSections';
import { candidateProfileStore } from './CandidateProfileStore';
import { observer } from 'mobx-react';
import { notificationStore } from '../../layout/notifications/NotificationStore';
import { hasPermission, PERMISSIONS } from 'shared/security/rolePermissions';
import {
	INVITE_CANDIDATE_LABEL,
	openInviteCandidateModal
} from 'ats/pages/applicants/components/ApplicantPublicProfile';

@withRouter
@observer
export class CandidateProfile extends React.Component {
	profileScrollRef = React.createRef();

	componentDidMount() {
		this.loadCandidate();
	}

	componentWillUnmount() {
		candidateProfileStore.reset();
	}

	async loadCandidate() {
		const candidateId = this.props.match.params.id;
		const result = await candidatesStore.get(candidateId);
		candidateProfileStore.setCandidate(result.data);
	}

	render() {
		const candidate = candidateProfileStore.candidate;

		if (!candidate) {
			return <Loader type="page-loader" />;
		}

		if (!candidate.candidateProfile) {
			return (
				<div className="AdminPortal" ref={this.profileScrollRef}>
					<div className="CandidateProfile">
						<div className="candidate-profile-unavailable">
							<p>
								This candidate does not have a Public Profile yet. Create it
								from the candidate's Profile tab or invite them to MIRA.
							</p>
						</div>
					</div>
				</div>
			);
		}

		return (
			<div className="AdminPortal" ref={this.profileScrollRef}>
				<div className="CandidateProfile">
					{this.renderNotRegisteredBadge()}
					<CandidateProfileBio
						candidate={candidate}
						isEdit={this.props.isEdit}
						onConfigChange={this.handleConfigChange}
						showOnlyOptimalWorkshift={true}
						scrollContainerRef={this.profileScrollRef}
					/>
					{this.renderCandidateProfileInfo()}
				</div>
			</div>
		);
	}

	renderNotRegisteredBadge() {
		const candidate = candidateProfileStore.candidate;
		if (!this.props.isEdit || candidate.userId) {
			return null;
		}

		const applicant = candidate.applicant;
		const canInvite =
			applicant?.id && hasPermission(PERMISSIONS.APPLICANT_EDIT);

		return (
			<div className="public-profile-status-banner candidate-profile-not-registered">
				<div className="public-profile-status-banner__text">
					<p className="public-profile-status-banner__title">Not registered</p>
					<p className="public-profile-status-banner__detail">
						This Public Profile was created by a recruiter. The candidate hasn't
						registered in MIRA yet.
					</p>
				</div>
				{canInvite && (
					<div className="public-profile-status-banner__actions">
						<button
							type="button"
							className="ats-button-soft"
							onClick={() => openInviteCandidateModal(applicant)}>
							{INVITE_CANDIDATE_LABEL}
						</button>
					</div>
				)}
			</div>
		);
	}

	renderCandidateProfileInfo() {
		const candidate = candidateProfileStore.candidate;

		if (
			candidate.candidateExperiences.length > 0 ||
			candidate.candidateEducations.length > 0 ||
			candidate.candidateCertifications.length > 0 ||
			candidate.candidateTestResults.length > 0 ||
			this.props.isEdit
		) {
			return (
				<CandidateProfileSections
					onConfigChange={this.handleConfigChange}
					onExperienceConfigChange={this.handleExperienceConfigChange}
					onEducationConfigChange={this.handleEducationConfigChange}
					onCertificationConfigChange={this.handleCertificationConfigChange}
					onTestResultConfigChange={this.handleTestResultConfigChange}
					candidate={candidateProfileStore.candidate}
					isEdit={this.props.isEdit}
				/>
			);
		}
		return null;
	}

	handleConfigChange = async (property, newValue) => {
		const { id } = candidateProfileStore.candidate.candidateProfile;
		const request = {
			update: { config: property, newValue: newValue },
			candidateId: candidateProfileStore.candidate.id
		};
		const result = await candidateProfileStore.updateProfile(id, request);
		return this.handleSuccessFailure(result);
	};

	handlePublish = async status => {
		const { id } = candidateProfileStore.candidate.candidateProfile;
		const request = { status: status };
		const result = await candidateProfileStore.publishProfile(id, request);
		return this.handleSuccessFailure(result);
	};

	handleExperienceConfigChange = async (id, newValue) => {
		const request = {
			update: { newValue: newValue }
		};
		const result = await candidateProfileStore.updateExperience(id, request);
		return this.handleSuccessFailure(result);
	};

	handleEducationConfigChange = async (id, newValue) => {
		const request = {
			update: { newValue: newValue }
		};
		const result = await candidateProfileStore.updateEducation(id, request);
		return this.handleSuccessFailure(result);
	};

	handleCertificationConfigChange = async (id, newValue) => {
		const request = {
			update: { newValue: newValue }
		};
		const result = await candidateProfileStore.updateCertification(id, request);
		return this.handleSuccessFailure(result);
	};

	handleTestResultConfigChange = async (id, newValue) => {
		const request = {
			update: { newValue: newValue }
		};
		const result = await candidateProfileStore.updateTestResult(id, request);
		return this.handleSuccessFailure(result);
	};

	async handleSuccessFailure(result) {
		if (result.status === 'success') {
			notificationStore.pushNotification({
				type: notificationStore.SUCCESS_NOTIFICATION,
				title: 'Candidate Profile',
				message: 'The candidate profile has been saved.'
			});
			await candidateProfileStore.reloadCandidate();
			return true;
		} else {
			notificationStore.pushNotification({
				type: notificationStore.ERROR_NOTIFICATION,
				title: 'Candidate Profile',
				message:
					'An error occurred updating candidate profile. Please try again.'
			});
			await candidateProfileStore.reloadCandidate();
			return false;
		}
	}
}
